import React, { useRef, CSSProperties } from 'react'
import { ChevronDown, ChevronUp, Image as ImageIcon, X, Plus } from 'lucide-react'
import { StyledCard, CardTitle, CollapsePanelHeader } from '../style'

export interface BeneficiamentoNota {
  localId: number
  id?: number
  sectorId: number
  description: string
  imageFiles: File[]
  imageUrls: string[]
  imageOriginalUrls: string[]
  imagePreviews: string[]
}

export interface Sector {
  id: number
  name: string
}

interface Props {
  notes: BeneficiamentoNota[]
  onChange: (notes: BeneficiamentoNota[]) => void
  sectors: Sector[]
  isOpen: boolean
  onToggle: () => void
  title?: string
}

let _localIdCounter = 1
export function newBeneficiamentoNota(sectorId: number): BeneficiamentoNota {
  return {
    localId: _localIdCounter++,
    sectorId,
    description: '',
    imageFiles: [],
    imageUrls: [],
    imageOriginalUrls: [],
    imagePreviews: [],
  }
}

const styles: Record<string, CSSProperties> = {
  noteCard: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  noteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  noteLabel: {
    backgroundColor: '#f3e8ff',
    color: '#7c3aed',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 100,
    whiteSpace: 'nowrap' as const,
    letterSpacing: '0.5px',
  },
  sectorSelect: {
    backgroundColor: '#f3e8ff',
    color: '#7c3aed',
    border: '1px solid #ddd6fe',
    borderRadius: 100,
    fontSize: 13,
    fontWeight: 600,
    padding: '4px 28px 4px 12px',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237c3aed' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '12px',
  },
  removeBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    padding: 2,
    borderRadius: 4,
    lineHeight: 1,
  },
  textarea: {
    width: '100%',
    minHeight: 80,
    padding: '10px 14px',
    fontSize: 14,
    color: '#374151',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    resize: 'vertical' as const,
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    lineHeight: '1.5',
  },
  imagesRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
    marginTop: 12,
  },
  imageThumb: {
    position: 'relative' as const,
    width: 72,
    height: 72,
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  imageThumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
  },
  removeImageBtn: {
    position: 'absolute' as const,
    top: 2,
    right: 2,
    background: 'rgba(0,0,0,0.55)',
    border: 'none',
    borderRadius: '50%',
    width: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  addImageBox: {
    width: 72,
    height: 72,
    border: '1.5px dashed #d1d5db',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    gap: 4,
    backgroundColor: '#fff',
  },
  addImageLabel: {
    fontSize: 11,
    color: '#9ca3af',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  addNotaBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#0090d3',
    fontSize: 14,
    fontWeight: 600,
    padding: '8px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
}

const Beneficiamento: React.FC<Props> = ({ notes, onChange, sectors, isOpen, onToggle, title = 'Beneficiamento' }) => {
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({})

  const defaultSectorId = sectors.length > 0 ? sectors[0].id : 0

  const addNota = () => {
    onChange([...notes, newBeneficiamentoNota(defaultSectorId)])
  }

  const removeNota = (localId: number) => {
    const nota = notes.find(n => n.localId === localId)
    if (nota) {
      nota.imagePreviews.forEach(p => URL.revokeObjectURL(p))
    }
    onChange(notes.filter(n => n.localId !== localId))
  }

  const updateNota = (localId: number, patch: Partial<BeneficiamentoNota>) => {
    onChange(notes.map(n => (n.localId === localId ? { ...n, ...patch } : n)))
  }

  const handleFilesChange = (localId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (!files.length) return
    const nota = notes.find(n => n.localId === localId)
    if (!nota) return
    const newPreviews = files.map(f => URL.createObjectURL(f))
    updateNota(localId, {
      imageFiles: [...nota.imageFiles, ...files],
      imagePreviews: [...nota.imagePreviews, ...newPreviews],
    })
    e.target.value = ''
  }

  const removeNewImage = (localId: number, idx: number) => {
    const nota = notes.find(n => n.localId === localId)
    if (!nota) return
    URL.revokeObjectURL(nota.imagePreviews[idx])
    const imageFiles = nota.imageFiles.filter((_, i) => i !== idx)
    const imagePreviews = nota.imagePreviews.filter((_, i) => i !== idx)
    updateNota(localId, { imageFiles, imagePreviews })
  }

  const removeExistingImage = (localId: number, idx: number) => {
    const nota = notes.find(n => n.localId === localId)
    if (!nota) return
    updateNota(localId, {
      imageUrls: nota.imageUrls.filter((_, i) => i !== idx),
      imageOriginalUrls: nota.imageOriginalUrls.filter((_, i) => i !== idx),
    })
  }

  return (
    <StyledCard>
      <CollapsePanelHeader $open={isOpen} onClick={onToggle}>
        <CardTitle>{title}</CardTitle>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </CollapsePanelHeader>

      {isOpen && (
        <>
          {notes.map((nota, index) => (
            <div key={nota.localId} style={styles.noteCard}>
              <div style={styles.noteHeader}>
                <span style={styles.noteLabel}>NOTA {index + 1}</span>
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <select
                    value={nota.sectorId || ''}
                    onChange={e => updateNota(nota.localId, { sectorId: Number(e.target.value) })}
                    onClick={e => e.stopPropagation()}
                    style={{
                      ...styles.sectorSelect,
                      color: nota.sectorId ? '#7c3aed' : '#9ca3af',
                    }}
                  >
                    <option value='' disabled>Selecione o setor</option>
                    {sectors.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  type='button'
                  onClick={e => { e.stopPropagation(); removeNota(nota.localId) }}
                  style={styles.removeBtn}
                  title='Remover nota'
                >
                  <X size={16} />
                </button>
              </div>

              <textarea
                value={nota.description}
                onChange={e => updateNota(nota.localId, { description: e.target.value })}
                onClick={e => e.stopPropagation()}
                placeholder='Descreva o beneficiamento, instrução ou observação...'
                style={styles.textarea}
                onFocus={e => { e.currentTarget.style.borderColor = '#0090d3' }}
                onBlur={e => { e.currentTarget.style.borderColor = '#d1d5db' }}
              />

              <div style={styles.imagesRow}>
                {nota.imageUrls.map((url, i) => (
                  <div key={`existing-${i}`} style={styles.imageThumb}>
                    <img src={url} alt={`imagem-${i + 1}`} style={styles.imageThumbImg} />
                    <button
                      type='button'
                      style={styles.removeImageBtn}
                      onClick={e => { e.stopPropagation(); removeExistingImage(nota.localId, i) }}
                      title='Remover imagem'
                    >
                      <X size={10} color='#fff' />
                    </button>
                  </div>
                ))}

                {nota.imagePreviews.map((preview, i) => (
                  <div key={`new-${i}`} style={styles.imageThumb}>
                    <img src={preview} alt={`novo-${i + 1}`} style={styles.imageThumbImg} />
                    <button
                      type='button'
                      style={styles.removeImageBtn}
                      onClick={e => { e.stopPropagation(); removeNewImage(nota.localId, i) }}
                      title='Remover imagem'
                    >
                      <X size={10} color='#fff' />
                    </button>
                  </div>
                ))}

                <div
                  style={styles.addImageBox}
                  onClick={e => { e.stopPropagation(); fileInputRefs.current[nota.localId]?.click() }}
                  title='Adicionar foto'
                >
                  <ImageIcon size={22} color='#9ca3af' />
                  <span style={styles.addImageLabel}>FOTO</span>
                </div>

                <input
                  ref={el => { fileInputRefs.current[nota.localId] = el }}
                  type='file'
                  accept='image/*'
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => handleFilesChange(nota.localId, e)}
                />
              </div>
            </div>
          ))}

          <button
            type='button'
            onClick={e => { e.stopPropagation(); addNota() }}
            style={styles.addNotaBtn}
          >
            <Plus size={16} />
            Adicionar Nota
          </button>
        </>
      )}
    </StyledCard>
  )
}

export default Beneficiamento
